import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap, ShieldCheck } from "lucide-react";
import SectionReveal from "./SectionReveal";

const PONY_GTX =
  "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/7hppphkn_imgi_1_Pony_GTX_Blau_-neues-Logo-Tankdeckel-2023-scaled.jpg";
const PONY_CROSS =
  "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/1yri11l6_imgi_2_Cross_Schwarz-Logo-ab-2022-scaled.jpg";
const BLINKER_VORNE =
  "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/uzzfl4uf_imgi_3_Pony_Mofablinker_vorne-5-scaled.jpg";
const BLINKER_HINTEN =
  "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/zbd5dxfh_imgi_4_Pony_Mofablinker_hinten-scaled.jpg";
const SCHALTER =
  "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/ff6l1vtm_imgi_5_Pony_Schalter_Mofablinker-scaled.jpg";

const MODELS = [
  {
    name: "Pony GTX",
    price: "ab CHF 3'598.-",
    img: PONY_GTX,
    bg: "from-[#eef2f7] to-[#d4dde8]",
    specs: [
      ["Motor", "Beta 2-Takt 2VA"],
      ["Sitzhöhe", "820 — 890 mm"],
      ["Anlasser", "mechanisch · elektronisch optional"],
      ["Bereifung", "2,25 × 17"],
      ["Tankinhalt", "5,5 Liter"],
      ["Bremsen", "Trommelbremsen"],
    ],
  },
  {
    name: "Pony Cross",
    price: "ab CHF 3'498.-",
    img: PONY_CROSS,
    bg: "from-[#f0eee8] to-[#dad7cf]",
    specs: [
      ["Motor", "Beta 2-Takt 2VA"],
      ["Sitzhöhe", "820 — 890 mm"],
      ["Anlasser", "mechanisch · elektronisch optional"],
      ["Bereifung", "2,25 × 16"],
      ["Tankinhalt", "4,8 Liter"],
      ["Bremsen", "Trommelbremsen"],
    ],
  },
];

export default function Pony() {
  return (
    <section
      id="pony"
      className="section relative bg-[var(--anthracite)] text-[var(--cream)] overflow-hidden"
      data-testid="pony-section"
    >
      <div className="absolute top-0 left-0 right-0 tricolore h-[3px]" />

      <div className="container-wide">
        {/* Trust strip */}
        <SectionReveal>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-10 mb-12 border-b border-[var(--cream)]/15">
            <div className="flex items-center gap-3">
              <ShieldCheck size={18} strokeWidth={1.3} className="text-[var(--sage-soft)]" />
              <p className="font-serif text-xl md:text-2xl italic">
                Schweizer Kult-Mofa —{" "}
                <span className="not-italic text-[var(--sage-soft)] font-medium">
                  robust, ehrlich, zeitlos.
                </span>
              </p>
            </div>
            <span className="overline !text-[var(--sage-soft)] whitespace-nowrap">
              2 Modelle · 1 Elektrokit
            </span>
          </div>
        </SectionReveal>

        {/* Models grid — cleaner, smaller images */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-20">
          {MODELS.map((m, i) => (
            <SectionReveal key={m.name} delay={i}>
              <motion.div
                whileHover={{ y: -4 }}
                transition={{ duration: 0.4 }}
                className="relative bg-[var(--cream)] text-[var(--anthracite)] overflow-hidden h-full flex flex-col"
                data-testid={`pony-card-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className={`relative aspect-[16/10] bg-gradient-to-br ${m.bg} grain overflow-hidden`}
                >
                  <img
                    src={m.img}
                    alt={m.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain p-8"
                  />
                </div>
                <div className="p-6 md:p-8 flex-1 flex flex-col">
                  <div className="flex items-baseline justify-between mb-5">
                    <h3 className="font-serif text-3xl md:text-4xl leading-none">
                      {m.name}
                    </h3>
                    <span className="font-serif text-lg text-[var(--sage-dark)] whitespace-nowrap">
                      {m.price}
                    </span>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-[var(--border)] pt-5 flex-1">
                    {m.specs.map(([k, v]) => (
                      <div key={k}>
                        <dt className="text-[9px] uppercase tracking-[0.2em] text-[var(--muted-fg)]">
                          {k}
                        </dt>
                        <dd className="font-sans text-xs md:text-sm mt-1 leading-snug">
                          {v}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <Link
                    to="/casa-markus"
                    className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] border-b border-[var(--anthracite)] pb-1 self-start hover:border-[var(--sage-dark)] hover:text-[var(--sage-dark)] transition-colors"
                    data-testid={`pony-cta-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    Probefahrt anfragen
                    <ArrowUpRight size={13} strokeWidth={1.5} />
                  </Link>
                </div>
              </motion.div>
            </SectionReveal>
          ))}
        </div>

        {/* Elektrokit Upsell */}
        <SectionReveal>
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center border-t border-[var(--cream)]/15 pt-16">
            <div className="lg:col-span-5">
              <span className="overline !text-[var(--sage-soft)] inline-flex items-center gap-2">
                <Zap size={12} strokeWidth={1.5} /> Optional · Upgrade
              </span>
              <h3 className="font-serif text-4xl md:text-5xl mt-5 leading-[1]">
                Das Elektrokit.
              </h3>
              <p className="mt-5 text-[var(--cream)]/70 leading-relaxed max-w-md text-sm md:text-base">
                E-Starter, Blinker, Horn und Bremslicht — passt auf beide Modelle. Optional für{" "}
                <span className="text-[var(--cream)] font-medium">+ CHF 366.-</span>.
              </p>
              <Link
                to="/casa-markus"
                className="inline-flex items-center gap-2 mt-6 text-[11px] uppercase tracking-[0.22em] border-b border-[var(--cream)]/40 pb-1 hover:text-[var(--sage-soft)] hover:border-[var(--sage-soft)] transition-colors"
                data-testid="elektrokit-cta"
              >
                Beratung anfragen
                <ArrowUpRight size={13} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-3 gap-2 md:gap-3">
              {[BLINKER_VORNE, BLINKER_HINTEN, SCHALTER].map((src, i) => (
                <div
                  key={src}
                  className="aspect-square overflow-hidden bg-[var(--cream-alt)] grain relative"
                  data-testid={`elektrokit-img-${i}`}
                >
                  <img
                    src={src}
                    alt="Elektrokit Detail"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                  />
                </div>
              ))}
            </div>
          </div>
        </SectionReveal>
      </div>
    </section>
  );
}
